// リップシンク（口パク）ユーティリティ
// 音声の再生と口の動きを同期させる機能

export class LipSyncManager {
  constructor() {
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.isAnalyzing = false;
    this.onMouthUpdate = null;
  }

  // Web Audio APIを初期化
  async initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      
      // console.log('Audio context initialized for lip sync'); // デバッグ用
      return true;
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
      return false;
    }
  }

  // 音声要素にリップシンクを設定
  attachToAudio(audioElement, onMouthUpdate) {
    if (!this.audioContext || !this.analyser) {
      console.warn('Audio context not initialized');
      return false;
    }

    this.onMouthUpdate = onMouthUpdate;

    try {
      // 音声要素からオーディオソースを作成
      const source = this.audioContext.createMediaElementSource(audioElement);
      source.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);

      // 音声の開始・終了イベントリスナー
      audioElement.addEventListener('play', () => this.startAnalysis());
      audioElement.addEventListener('ended', () => this.stopAnalysis());
      audioElement.addEventListener('pause', () => this.stopAnalysis());

      return true;
    } catch (error) {
      console.error('Failed to attach audio for lip sync:', error);
      return false;
    }
  }

  // 音声解析開始
  startAnalysis() {
    this.isAnalyzing = true;
    this.analyze();
  }

  // 音声解析停止
  stopAnalysis() {
    this.isAnalyzing = false;
    if (this.onMouthUpdate) {
      this.onMouthUpdate(0); // 口を閉じる
    }
  }

  // リアルタイム音声解析
  analyze() {
    if (!this.isAnalyzing) return;

    this.analyser.getByteFrequencyData(this.dataArray);
    
    // 低域〜中域の音量を取得（人間の声の主要な周波数帯域）
    const voiceRange = this.dataArray.slice(5, 50);
    const averageVolume = voiceRange.reduce((sum, value) => sum + value, 0) / voiceRange.length;
    
    // 音量を0-1の範囲に正規化
    const normalizedVolume = Math.min(averageVolume / 128, 1);
    
    // 口の開き具合を計算（最小値を設定してより自然に）
    const mouthOpenness = Math.max(normalizedVolume * 1.5, 0.1);
    
    if (this.onMouthUpdate) {
      this.onMouthUpdate(mouthOpenness);
    }

    // 次のフレームで再度解析
    requestAnimationFrame(() => this.analyze());
  }

  // より高度な感情解析（音声のピッチや強度から表情を推定）
  getEmotionFromAudio() {
    if (!this.dataArray) return 'neutral';

    const highFreq = this.dataArray.slice(80, 120);
    const midFreq = this.dataArray.slice(20, 60);
    const lowFreq = this.dataArray.slice(0, 20);

    const highAvg = highFreq.reduce((sum, val) => sum + val, 0) / highFreq.length;
    const midAvg = midFreq.reduce((sum, val) => sum + val, 0) / midFreq.length;
    const lowAvg = lowFreq.reduce((sum, val) => sum + val, 0) / lowFreq.length;

    // 簡単な感情推定ロジック
    if (highAvg > 100 && midAvg > 80) return 'excited';
    if (lowAvg > 80 && midAvg < 60) return 'calm';
    if (midAvg > 90) return 'speaking';
    
    return 'neutral';
  }

  // メモリクリーンアップ
  destroy() {
    this.stopAnalysis();
    if (this.audioContext) {
      this.audioContext.close();
    }
  }
}

// 単純なリップシンク用のヘルパー関数
export const createSimpleLipSync = (audioElement, mouthCallback) => {
  let interval;
  
  const startLipSync = () => {
    interval = setInterval(() => {
      // ランダムな口の動きでシンプルな口パクを再現
      const randomness = Math.random() * 0.8 + 0.2;
      mouthCallback(randomness);
    }, 150); // 150msごとに口の形を変更
  };

  const stopLipSync = () => {
    if (interval) {
      clearInterval(interval);
      mouthCallback(0); // 口を閉じる
    }
  };

  audioElement.addEventListener('play', startLipSync);
  audioElement.addEventListener('ended', stopLipSync);
  audioElement.addEventListener('pause', stopLipSync);

  return {
    destroy: () => {
      stopLipSync();
      audioElement.removeEventListener('play', startLipSync);
      audioElement.removeEventListener('ended', stopLipSync);
      audioElement.removeEventListener('pause', stopLipSync);
    }
  };
};

export default LipSyncManager;